import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { ImageLightbox } from '@/components/shared/image-lightbox';
import { Pagination } from '@/components/shared/pagination';
import { ErrorState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProgressBar } from '@/components/ui/progress-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { api, type Collection } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { uploadBlob } from '@/lib/upload';
import { cn } from '@/utils/cn';
import { formatDate, formatDateTime } from '@/utils/format';

/** Matches MAX_MEMBER_FILES in the people service. */
const MAX_MEMBER_FILES = 20;
const PAGE_SIZE = 20;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const ACCEPTED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
]);
const ACCEPTED_NAME = /\.(jpe?g|png|webp|heic|heif|pdf)$/i;

interface MemberFile {
  id: string;
  fileName: string;
  contentType: string;
  kind: 'image' | 'document';
  downloadUrl: string | null;
  createdAt: string;
}

interface RegisterImage {
  id: string;
  registerKind: 'baptism' | 'confirmation';
  imageKind: 'person' | 'certificate';
  url: string;
  eventDate: string;
}

const registerImageLabel = (
  image: RegisterImage,
  t: (key: string, options: { date: string }) => string,
) =>
  t(`members.detail.files.register.${image.registerKind}.${image.imageKind}`, {
    date: formatDate(image.eventDate),
  });

const isAcceptedFile = (file: File): boolean =>
  ACCEPTED_TYPES.has(file.type) || ACCEPTED_NAME.test(file.name);

const isImage = (file: MemberFile): boolean => file.kind === 'image';

const isPdf = (file: MemberFile): boolean =>
  file.contentType === 'application/pdf' || file.fileName.toLowerCase().endsWith('.pdf');

export const MemberFilesCard = ({
  memberId,
  memberName,
  canEdit,
  status,
}: {
  memberId: string;
  memberName: string;
  canEdit: boolean;
  status: string;
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [fileError, setFileError] = useState<string | undefined>();
  const [uploadProgress, setUploadProgress] = useState<number | undefined>();
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [uploadingCount, setUploadingCount] = useState<{ current: number; total: number } | null>(
    null,
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [pdfId, setPdfId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<MemberFile | null>(null);

  const canManage = canEdit && (status === 'active' || status === 'inactive');

  const files = useQuery({
    queryKey: ['people', 'member', memberId, 'files', page],
    queryFn: () =>
      api.get<Collection<MemberFile, { registerImages: RegisterImage[] }>>(
        `/people/members/${memberId}/files`,
        {
          query: { page, pageSize: PAGE_SIZE },
        },
      ),
    placeholderData: (previous) => previous,
  });

  const reportError = (error: unknown) => {
    toast.error(error instanceof ApiError ? tErrors(error.code, error.params) : tErrors('UNEXPECTED'));
  };

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['people', 'member', memberId, 'files'] });

  const upload = useMutation({
    mutationFn: async (selected: File[]) => {
      for (const [index, file] of selected.entries()) {
        setUploadingName(file.name);
        setUploadingCount({ current: index + 1, total: selected.length });
        setUploadProgress(Math.round((index / selected.length) * 100));
        const uploaded = await uploadBlob({
          purpose: 'member_file',
          blob: file,
          fileName: file.name,
        });
        await api.post(`/people/members/${memberId}/files`, {
          fileKey: uploaded.key,
          fileName: file.name,
          contentType: uploaded.contentType,
        });
        setUploadProgress(Math.round(((index + 1) / selected.length) * 100));
      }
      return selected.length;
    },
    onSuccess: (count) => {
      setPage(1);
      setAddOpen(false);
      setFileError(undefined);
      toast.success(t('members.detail.files.saved', { count }));
      void refresh();
    },
    onError: (error) => {
      void refresh();
      reportError(error);
    },
    onSettled: () => {
      setUploadProgress(undefined);
      setUploadingName(null);
      setUploadingCount(null);
    },
  });

  const remove = useMutation({
    mutationFn: (fileId: string) => api.delete(`/people/member-files/${fileId}`),
    onSuccess: () => {
      const remaining = (files.data?.data.length ?? 1) - 1;
      if (remaining <= 0 && page > 1) setPage(page - 1);
      setPendingRemove(null);
      setLightboxIndex(null);
      setPdfId(null);
      toast.success(t('members.detail.files.removed'));
      void refresh();
    },
    onError: reportError,
  });

  const rows = files.data?.data ?? [];
  const registerImages = files.data?.totals?.registerImages ?? [];
  const images = rows.filter((file) => isImage(file) && file.downloadUrl);
  const lightboxImages = [
    ...registerImages.map((image) => image.url),
    ...images.map((file) => file.downloadUrl ?? ''),
  ];
  const pdfFile = rows.find((file) => file.id === pdfId && isPdf(file) && file.downloadUrl) ?? null;
  const atMax = (files.data?.page.totalItems ?? 0) >= MAX_MEMBER_FILES;

  const chooseFiles = (incoming: File[]) => {
    const accepted = incoming.filter(isAcceptedFile);
    const room = MAX_MEMBER_FILES - (files.data?.page.totalItems ?? 0);
    if (accepted.length === 0) {
      setFileError(tErrors('UPLOAD_CONTENT_TYPE_NOT_ALLOWED'));
      return;
    }
    if (room <= 0) {
      setFileError(tErrors('MEMBER_FILE_LIMIT', { max: MAX_MEMBER_FILES }));
      return;
    }
    const next = accepted.slice(0, room);
    if (next.length < incoming.length) {
      setFileError(
        next.length < accepted.length
          ? tErrors('MEMBER_FILE_LIMIT', { max: MAX_MEMBER_FILES })
          : tErrors('UPLOAD_CONTENT_TYPE_NOT_ALLOWED'),
      );
    } else {
      setFileError(undefined);
    }
    upload.mutate(next);
  };

  const previewLabel = (file: MemberFile) =>
    t('members.detail.files.preview', {
      name: file.fileName,
      added: formatDateTime(file.createdAt),
    });

  const activeRegisterImage =
    lightboxIndex !== null ? registerImages[lightboxIndex] : undefined;
  const activeFile =
    lightboxIndex !== null ? images[lightboxIndex - registerImages.length] : undefined;
  const lightboxCaption = activeRegisterImage
    ? registerImageLabel(activeRegisterImage, t)
    : (activeFile?.fileName ?? null);
  let lightboxAlt = '';
  if (activeRegisterImage) lightboxAlt = registerImageLabel(activeRegisterImage, t);
  else if (activeFile) lightboxAlt = previewLabel(activeFile);

  const uploadLabel =
    uploadingCount && uploadingCount.total > 1
      ? t('members.detail.files.uploadingMany', uploadingCount)
      : t('members.detail.files.uploading', { name: uploadingName ?? '' });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-card-title font-semibold">{t('members.detail.files.title')}</h2>
          <p className="text-body-sm text-fg-secondary">{t('members.detail.files.description')}</p>
        </div>
        {canManage ? (
          <Button
            type="button"
            size="sm"
            leadingIcon={<Plus className="size-4" aria-hidden />}
            disabled={upload.isPending || files.isPending || atMax}
            onClick={() => {
              setFileError(undefined);
              setAddOpen(true);
            }}
          >
            {t('members.detail.files.add')}
          </Button>
        ) : null}
      </div>

      {canEdit && !canManage ? (
        <p className="text-body-sm text-fg-secondary">{t('members.detail.files.locked')}</p>
      ) : null}

      {upload.isPending && !addOpen ? (
        <ProgressBar
          value={uploadProgress ?? 0}
          label={uploadLabel}
          valueText={uploadingName ?? undefined}
          size="sm"
          className="max-w-sm"
        />
      ) : null}

      {fileError && !addOpen ? (
        <p className="text-caption text-danger-fg" role="alert">
          {fileError}
        </p>
      ) : null}

      {atMax ? (
        <p className="text-body-sm text-fg-secondary">
          {tErrors('MEMBER_FILE_LIMIT', { max: MAX_MEMBER_FILES })}
        </p>
      ) : null}

      {files.isPending ? (
        <div className="flex gap-3" role="status">
          <span className="sr-only">{tCommon('state.loading')}</span>
          <Skeleton className="size-24 rounded-lg" />
          <Skeleton className="size-24 rounded-lg" />
          <Skeleton className="size-24 rounded-lg" />
        </div>
      ) : null}

      {files.isError ? (
        <ErrorState
          title={t('members.detail.files.loadError')}
          onRetry={() => void files.refetch()}
          retryLabel={tCommon('action.retry')}
        />
      ) : null}

      {!files.isPending && !files.isError && rows.length === 0 && registerImages.length === 0 ? (
        <p className="text-body-sm text-fg-secondary">{t('members.detail.files.emptyBody')}</p>
      ) : null}

      {!files.isPending && !files.isError && registerImages.length > 0 ? (
        <ul className="flex flex-wrap gap-3">
          {registerImages.map((image, index) => {
            const label = registerImageLabel(image, t);
            return (
              <li key={image.id} className="w-24">
                <button
                  type="button"
                  className="block size-24 cursor-zoom-in overflow-hidden rounded-lg bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                  aria-label={label}
                  onClick={() => {
                    setPdfId(null);
                    setLightboxIndex(index);
                  }}
                >
                  <img src={image.url} alt="" className="size-full object-cover" />
                </button>
                <p className="mt-1 line-clamp-2 text-caption text-fg-tertiary">{label}</p>
              </li>
            );
          })}
        </ul>
      ) : null}

      {!files.isPending && !files.isError && rows.length > 0 ? (
        <MemberFileList
          files={rows}
          canManage={canManage}
          removingId={remove.isPending ? (remove.variables ?? null) : null}
          previewLabel={previewLabel}
          removeLabel={t('members.detail.files.remove')}
          onOpenImage={(file) => {
            const index = images.findIndex((item) => item.id === file.id);
            if (index >= 0) {
              setPdfId(null);
              setLightboxIndex(registerImages.length + index);
            }
          }}
          onOpenPdf={(file) => {
            setLightboxIndex(null);
            setPdfId(file.id);
          }}
          onRemove={setPendingRemove}
        />
      ) : null}

      {files.data && files.data.page.totalItems > 0 ? (
        <Pagination
          page={page}
          pageSize={files.data.page.pageSize}
          totalItems={files.data.page.totalItems}
          totalPages={files.data.page.totalPages}
          onPageChange={setPage}
          disabled={files.isFetching || upload.isPending}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent size="md" className="overflow-visible" closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('members.detail.files.add')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-6">
            <FileDropzone
              multiple
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf"
              acceptLabel={t('members.detail.files.accept')}
              maxBytes={MAX_FILE_BYTES}
              disabled={upload.isPending || atMax}
              progress={upload.isPending ? uploadProgress : undefined}
              progressLabel={upload.isPending ? uploadLabel : undefined}
              error={fileError}
              labels={{
                prompt: t('members.detail.files.prompt'),
                remove: t('members.detail.files.remove'),
              }}
              onFiles={chooseFiles}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>

      <ImageLightbox
        images={lightboxImages}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onIndexChange={setLightboxIndex}
        caption={lightboxCaption}
        labels={{
          dialogLabel: tCommon('lightbox.title'),
          close: tCommon('lightbox.close'),
          previous: tCommon('lightbox.previous'),
          next: tCommon('lightbox.next'),
          count:
            lightboxIndex !== null
              ? tCommon('lightbox.imageCount', {
                  current: lightboxIndex + 1,
                  total: lightboxImages.length,
                })
              : '',
          imageAlt: lightboxAlt,
        }}
      />

      {pdfFile?.downloadUrl ? (
        <Dialog open onOpenChange={(open) => { if (!open) setPdfId(null); }}>
          <DialogContent size="lg" closeLabel={tCommon('action.close')}>
            <DialogHeader>
              <DialogTitle>{pdfFile.fileName}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <iframe
                title={pdfFile.fileName}
                src={pdfFile.downloadUrl}
                className="h-[min(60dvh,32rem)] w-full rounded-lg border border-border bg-surface"
              />
            </DialogBody>
            <DialogFooter>
              <Button asChild>
                <a href={pdfFile.downloadUrl} target="_blank" rel="noopener noreferrer">
                  {t('members.detail.files.open')}
                </a>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title={t('members.detail.files.removeTitle')}
        description={t('members.detail.files.removeBody', {
          fileName: pendingRemove?.fileName ?? '',
          name: memberName,
        })}
        confirmLabel={t('members.detail.files.remove')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (pendingRemove) remove.mutate(pendingRemove.id);
        }}
      />
    </div>
  );
};

const MemberFileList = ({
  files,
  canManage,
  removingId,
  previewLabel,
  removeLabel,
  onOpenImage,
  onOpenPdf,
  onRemove,
}: {
  files: MemberFile[];
  canManage: boolean;
  removingId: string | null;
  previewLabel: (file: MemberFile) => string;
  removeLabel: string;
  onOpenImage: (file: MemberFile) => void;
  onOpenPdf: (file: MemberFile) => void;
  onRemove: (file: MemberFile) => void;
}) => {
  const images = files.filter(isImage);
  const documents = files.filter((file) => !isImage(file));

  return (
    <div className="flex flex-col gap-4">
      {images.length > 0 ? (
        <ul className="flex flex-wrap gap-3">
          {images.map((file) => (
            <li key={file.id} className="w-24">
              <div className="relative size-24">
                <FileImageTile
                  file={file}
                  label={previewLabel(file)}
                  onOpen={() => onOpenImage(file)}
                />
                {canManage ? (
                  <RemoveFileButton
                    className="absolute end-0.5 top-0.5 z-10 size-7 bg-surface/90 text-fg shadow-e1 hover:bg-surface"
                    label={removeLabel}
                    pending={removingId === file.id}
                    onClick={() => onRemove(file)}
                  />
                ) : null}
              </div>
              <p className="mt-1 truncate text-caption text-fg-tertiary">{file.fileName}</p>
            </li>
          ))}
        </ul>
      ) : null}
      {documents.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {documents.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
            >
              <button
                type="button"
                className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                aria-label={previewLabel(file)}
                disabled={!file.downloadUrl || !isPdf(file)}
                onClick={() => onOpenPdf(file)}
              >
                <FileText aria-hidden className="size-4 shrink-0 text-fg-tertiary" />
                <span className="min-w-0">
                  <span className="block truncate text-body-sm text-fg">{file.fileName}</span>
                  <span className="block text-caption text-fg-tertiary">
                    {formatDateTime(file.createdAt)}
                  </span>
                </span>
              </button>
              {canManage ? (
                <RemoveFileButton
                  className="shrink-0"
                  label={removeLabel}
                  pending={removingId === file.id}
                  onClick={() => onRemove(file)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const FileImageTile = ({
  file,
  label,
  onOpen,
}: {
  file: MemberFile;
  label: string;
  onOpen: () => void;
}) => {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(file.downloadUrl) && !failed;

  return (
    <button
      type="button"
      className="block size-24 cursor-zoom-in overflow-hidden rounded-lg bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      aria-label={label}
      disabled={!showImage}
      onClick={onOpen}
    >
      {showImage ? (
        <img
          src={file.downloadUrl ?? undefined}
          alt=""
          className="size-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="flex size-full items-center justify-center p-2 text-center text-caption text-fg-secondary">
          {file.fileName}
        </span>
      )}
    </button>
  );
};

const RemoveFileButton = ({
  label,
  pending,
  onClick,
  className,
}: {
  label: string;
  pending: boolean;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    className={cn(
      'flex size-11 shrink-0 items-center justify-center rounded-md text-fg-tertiary hover:bg-surface-sunken hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
      className,
    )}
    aria-label={label}
    disabled={pending}
    onClick={onClick}
  >
    {pending ? <Spinner className="size-4" /> : <X className="size-4" aria-hidden />}
  </button>
);
